import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import { useApi } from '../lib/api';
import { formatBytes, formatCents, formatDate } from '../lib/format';

type Experience = {
  id: string;
  slug: string;
  organizer_hash: string;
  recipient_hash: string;
  recipient_name: string;
  occasion_type: string | null;
  status: string;
  created_at: string;
  published_at: string | null;
  organizer_email: string | null;
  payment_status: string | null;
  amount_cents: number | null;
  currency: string | null;
};

type Guest = { id: string; name: string; max_uploads: number; created_at: string; upload_count: number };
type Upload = {
  id: string;
  guest_id: string | null;
  media_type: string;
  status: string;
  file_size_bytes: string | null;
  duration_seconds: number | null;
  original_filename: string | null;
  error_message: string | null;
  created_at: string;
};
type Payment = {
  id: string;
  lemon_order_id: string;
  status: string;
  amount_cents: number;
  currency: string;
  raw_event: Record<string, unknown>;
  created_at: string;
};
type Counts = { timeline_items: number; characters: number; chat_groups: number };

type Resp = {
  experience: Experience;
  guests: Guest[];
  uploads: Upload[];
  payments: Payment[];
  counts: Counts;
};

type SizeCounts = { count: number; bytes: number };

type StorageResp = {
  db: { videos: SizeCounts; images: SizeCounts; total: SizeCounts };
  r2: { count: number; totalBytes: number; byPrefix: Record<string, SizeCounts> };
  avatars: { count: number; totalBytes: number; missing: number; breakdown: Record<string, SizeCounts> };
  diff: number;
  totalBytes: number;
};

type Tab = 'guests' | 'uploads' | 'payments' | 'storage';

export default function ExperienceDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, error, loading } = useApi<Resp>(id ? `/api/experiences/${id}` : null);
  const [tab, setTab] = useState<Tab>('guests');
  const [storagePath, setStoragePath] = useState<string | null>(null);
  const storage = useApi<StorageResp>(storagePath);

  return (
    <div>
      <Link to="/experiences" className="text-sm text-blue-700 hover:underline">← Back to experiences</Link>

      {loading && <p className="mt-4 text-sm text-slate-500">Loading…</p>}
      {error && <p className="mt-4 text-sm text-red-600">Error: {error.message}</p>}

      {data && (
        <>
          <PageHeader
            title={data.experience.recipient_name}
            subtitle={`${data.experience.occasion_type ?? 'no occasion'} · created ${formatDate(data.experience.created_at)}`}
            actions={<StatusBadge status={data.experience.status} />}
          />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <DetailCell label="Organizer" value={data.experience.organizer_email ?? '—'} />
            <DetailCell label="Slug" value={data.experience.slug} mono />
            <DetailCell
              label="Payment"
              value={
                data.experience.payment_status
                  ? `${data.experience.payment_status} · ${formatCents(data.experience.amount_cents ?? 0, data.experience.currency ?? 'EUR')}`
                  : '—'
              }
            />
            <DetailCell label="Published" value={formatDate(data.experience.published_at)} />
            <DetailCell label="Guests" value={String(data.guests.length)} />
            <DetailCell label="Uploads" value={String(data.uploads.length)} />
            <DetailCell label="Timeline items" value={String(data.counts.timeline_items)} />
            <DetailCell label="Characters / Groups" value={`${data.counts.characters} / ${data.counts.chat_groups}`} />
          </div>

          <div className="border-b border-slate-200 mb-4 flex gap-2">
            {(['guests', 'uploads', 'payments', 'storage'] as Tab[]).map((t) => (
              <button
                key={t}
                onClick={() => {
                  setTab(t);
                  if (t === 'storage' && !storagePath && id) setStoragePath(`/api/storage/${id}`);
                }}
                className={`px-3 py-2 text-sm border-b-2 -mb-px capitalize ${
                  tab === t
                    ? 'border-slate-900 text-slate-900 font-medium'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {tab === 'guests' && <GuestsTab guests={data.guests} />}
          {tab === 'uploads' && <UploadsTab uploads={data.uploads} guests={data.guests} />}
          {tab === 'payments' && <PaymentsTab payments={data.payments} />}
          {tab === 'storage' && (
            <StorageTab
              loading={storage.loading}
              error={storage.error}
              data={storage.data}
            />
          )}
        </>
      )}
    </div>
  );
}

function DetailCell({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-sm text-slate-900 ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}

function GuestsTab({ guests }: { guests: Guest[] }) {
  if (guests.length === 0) return <p className="text-sm text-slate-500">No guests yet.</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2 font-medium">Name</th>
            <th className="px-4 py-2 font-medium text-right">Uploads / Max</th>
            <th className="px-4 py-2 font-medium">Joined</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {guests.map((g) => (
            <tr key={g.id}>
              <td className="px-4 py-2">{g.name}</td>
              <td className="px-4 py-2 text-right tabular-nums">
                {g.upload_count} / {g.max_uploads}
              </td>
              <td className="px-4 py-2 text-slate-600">{formatDate(g.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UploadsTab({ uploads, guests }: { uploads: Upload[]; guests: Guest[] }) {
  const guestById = new Map(guests.map((g) => [g.id, g.name]));
  if (uploads.length === 0) return <p className="text-sm text-slate-500">No uploads.</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2 font-medium">Type</th>
            <th className="px-4 py-2 font-medium">Status</th>
            <th className="px-4 py-2 font-medium">Guest</th>
            <th className="px-4 py-2 font-medium text-right">Size</th>
            <th className="px-4 py-2 font-medium text-right">Duration</th>
            <th className="px-4 py-2 font-medium">Uploaded</th>
            <th className="px-4 py-2 font-medium">Error</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {uploads.map((u) => (
            <tr key={u.id}>
              <td className="px-4 py-2">{u.media_type}</td>
              <td className="px-4 py-2"><StatusBadge status={u.status} /></td>
              <td className="px-4 py-2">{u.guest_id ? (guestById.get(u.guest_id) ?? '—') : <span className="text-slate-500 italic">organizer</span>}</td>
              <td className="px-4 py-2 text-right tabular-nums">
                {u.file_size_bytes ? formatBytes(Number(u.file_size_bytes)) : '—'}
              </td>
              <td className="px-4 py-2 text-right tabular-nums">
                {u.duration_seconds ? `${u.duration_seconds.toFixed(1)}s` : '—'}
              </td>
              <td className="px-4 py-2 text-slate-600">{formatDate(u.created_at)}</td>
              <td className="px-4 py-2 text-red-600 text-xs max-w-xs truncate" title={u.error_message ?? ''}>
                {u.error_message ?? ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaymentsTab({ payments }: { payments: Payment[] }) {
  if (payments.length === 0) return <p className="text-sm text-slate-500">No payments.</p>;
  return (
    <div className="space-y-3">
      {payments.map((p) => (
        <details key={p.id} className="rounded-lg border border-slate-200 bg-white p-4">
          <summary className="cursor-pointer flex items-center justify-between">
            <div className="flex items-center gap-3">
              <StatusBadge status={p.status} />
              <span className="font-mono text-sm">{p.lemon_order_id}</span>
              <span className="text-sm text-slate-700">{formatCents(p.amount_cents, p.currency)}</span>
            </div>
            <span className="text-xs text-slate-500">{formatDate(p.created_at)}</span>
          </summary>
          <pre className="mt-3 overflow-x-auto text-xs bg-slate-50 p-3 rounded border border-slate-200">
            {JSON.stringify(p.raw_event, null, 2)}
          </pre>
        </details>
      ))}
    </div>
  );
}

function StorageTab({
  loading,
  error,
  data,
}: {
  loading: boolean;
  error: Error | null;
  data: StorageResp | null;
}) {
  if (loading) return <p className="text-sm text-slate-500">Scanning R2…</p>;
  if (error) return <p className="text-sm text-red-600">Error: {error.message}</p>;
  if (!data) return null;

  const mediaPrefixes = Object.entries(data.r2.byPrefix).sort((a, b) => b[1].bytes - a[1].bytes);
  const avatarBreakdown = Object.entries(data.avatars.breakdown).sort((a, b) => b[1].bytes - a[1].bytes);

  return (
    <div className="space-y-6">
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
        <div className="text-xs uppercase tracking-wide text-emerald-700">Total R2 storage (this experience)</div>
        <div className="mt-1 text-3xl font-semibold text-emerald-900">{formatBytes(data.totalBytes)}</div>
        <div className="mt-1 text-xs text-emerald-700">
          Media {formatBytes(data.r2.totalBytes)} · Avatars {formatBytes(data.avatars.totalBytes)}
          {data.avatars.missing > 0 && ` · ${data.avatars.missing} avatar(s) missing in R2`}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <DetailCell label="DB media" value={`${data.db.total.count} files · ${formatBytes(data.db.total.bytes)}`} />
        <DetailCell label="DB videos" value={`${data.db.videos.count} · ${formatBytes(data.db.videos.bytes)}`} />
        <DetailCell label="DB images" value={`${data.db.images.count} · ${formatBytes(data.db.images.bytes)}`} />
        <DetailCell
          label="R2 media"
          value={`${data.r2.count} · ${formatBytes(data.r2.totalBytes)}`}
        />
      </div>

      <div className="rounded-md border border-slate-200 bg-white p-4">
        <div className="text-xs uppercase tracking-wide text-slate-500">DB / R2 media diff</div>
        <div className={`mt-1 text-lg font-semibold ${Math.abs(data.diff) > 1024 * 1024 ? 'text-amber-700' : 'text-slate-900'}`}>
          {data.diff >= 0 ? '+' : '−'}{formatBytes(Math.abs(data.diff))}
        </div>
        <div className="mt-1 text-xs text-slate-500">
          Difference between R2 media bytes and DB-tracked uploads.file_size_bytes. Large positive values mean orphaned objects in R2; negative means DB rows without files.
        </div>
      </div>

      {avatarBreakdown.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <div className="px-4 py-2 text-xs uppercase tracking-wide text-slate-500 bg-slate-50 border-b border-slate-200">
            Avatars by type
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium text-right">Files</th>
                <th className="px-4 py-2 font-medium text-right">Bytes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {avatarBreakdown.map(([type, v]) => (
                <tr key={type}>
                  <td className="px-4 py-2 font-mono text-xs">avatars/{type}/</td>
                  <td className="px-4 py-2 text-right tabular-nums">{v.count}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatBytes(v.bytes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <div className="px-4 py-2 text-xs uppercase tracking-wide text-slate-500 bg-slate-50 border-b border-slate-200">
          Media by sub-prefix
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">R2 sub-prefix</th>
              <th className="px-4 py-2 font-medium text-right">Files</th>
              <th className="px-4 py-2 font-medium text-right">Bytes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {mediaPrefixes.map(([prefix, v]) => (
              <tr key={prefix}>
                <td className="px-4 py-2 font-mono text-xs">{prefix}/</td>
                <td className="px-4 py-2 text-right tabular-nums">{v.count}</td>
                <td className="px-4 py-2 text-right tabular-nums">{formatBytes(v.bytes)}</td>
              </tr>
            ))}
            {mediaPrefixes.length === 0 && (
              <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-500">No R2 media objects.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
